import { Card, Typography, Space, Flex, Tag } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { round100 } from 'shared/utils/numbers/round.ts';
import { CURRENCY_SIGN } from 'types/currencies';
import { OrderStatus } from 'types/order.ts';
import { CargoItemStatuses } from 'client-app/components/client-cargos/cargo-item-status';
import { InvoiceDocumentsListModal } from 'client-app/components/documents/invoice-documents-list-modal/invoice-documents-list-modal.tsx';
import { findContactsById } from 'client-app/shared/utils/contacts/find-contacts-by-id';
import styles from './client-orders-list.module.scss';
import { ManagerInfoPopover } from './components/manager-info-popover';
import { OrderCargoesDetails } from './components/order-cargoes-details';
import type { IClientOrdersListProps } from './types';

const { Title, Text } = Typography;

export const ClientOrdersList: React.FC<IClientOrdersListProps> = (props) => {
  const { clientOrders, contacts } = props;

  return (
    <Space direction='vertical' size={24} className={styles.contentWrapper}>
      {clientOrders.map((order) => {
        const contact = findContactsById(order.contactId, contacts);
        const originalPrice = round100(order.freight + (order.documentPrice ?? 0));
        const hasFine = (order.fine ?? 0) > 0;
        const totalToPay = round100(originalPrice + (order.fine ?? 0));

        return (
          <Card key={order.id} className={styles.card}>
            <Space direction='vertical' size={0} className={styles.spaceVertical}>
              <Flex justify='space-between' align='start'>
                <Flex gap={6} vertical>
                  <Space align='end'>
                    <Title level={5} className={styles.title}>
                      {order.clientOrderNumber || order.compositeId}
                    </Title>
                    <span className={styles.dot}> • </span>
                    {order.clientOrderNumber && (
                      <>
                        <Text className={styles.secondaryText}>{order.compositeId}</Text>
                        <span className={styles.dot}> • </span>
                      </>
                    )}
                    <Text className={styles.secondaryText}>Manager: {order.user.name}</Text>
                    <ManagerInfoPopover user={order.user} />
                    {contact && (
                      <>
                        <span className={styles.dot}> • </span>
                        <Text className={styles.secondaryText}>{contact.name}</Text>
                      </>
                    )}
                  </Space>
                  <Space size={4}>
                    {hasFine && (
                      <Tag color='error' icon={<WarningOutlined />} className={styles.fineTag}>
                        Penalty {round100(order.fine ?? 0)} {CURRENCY_SIGN[order.currency]}
                      </Tag>
                    )}
                    <CargoItemStatuses statuses={order.cargosStatuses} />
                  </Space>
                </Flex>

                <Space direction='horizontal'>
                  {order.status === OrderStatus.delivered ? <InvoiceDocumentsListModal orderId={order.id} /> : null}
                  <Space direction='vertical' align='end' className={styles.documentPriceContainer}>
                    {hasFine && (
                      <Text type='secondary' delete>
                        {originalPrice} {CURRENCY_SIGN[order.currency]}
                      </Text>
                    )}
                    <Title level={3} className={styles.title}>
                      {totalToPay} {CURRENCY_SIGN[order.currency]}
                    </Title>
                    <Text type='secondary' className={styles.createdDate}>
                      {new Date(order.createdAt).toLocaleDateString()}
                    </Text>
                  </Space>
                </Space>
              </Flex>

              {order.cargosStatuses.length > 0 && <OrderCargoesDetails orderId={order.id} />}
            </Space>
          </Card>
        );
      })}
    </Space>
  );
};
